<template>
  <div class="products-quantities-selector-v3">
    <div class="products-matrix">
      <div class="products">
        <div v-if="!isQuantityIncreaseAllowed" class="warning-banner">
          {{ increaseQuantitiesNotAllowedMessage }}
        </div>

        <!-- Quantities Navigation Tab -->
        <div
          v-if="navBarItems.length"
          class="navbar-background"
          id="quantitiesNavBar"
          :style="`top: ${mainGroupGenderNavBarStickyOffset}px`">
          <QuantitiesNavBar
            ref="quantitiesNavBar"
            :items="navBarItems"
            :show-wholesale-price="showWholesalePrice"
            :show-excel-upload="showUploadButton"
            v-on:toggle-show-wholesale-price="toggleShowWholesalePrice"
            v-on:open-excel-upload="onExcelUploadClick"
            :show-quantities="true"
            @selected="onNavBarSelected" />
        </div>

        <!-- Loader -->
        <div v-if="loading" class="loader"></div>
        <div v-else-if="error" class="error-message">{{ $t('matrix.error.no_products_available') }}</div>

        <div v-else class="product-groups-cnt">
          <!-- Main Group/Gender/Product Group Matrix Rendering -->
          <template v-for="[groupGenderKey, groupGender] in groupedMatrix" :key="groupGenderKey">
            <template v-if="`${groupGenderKey}` === selectedNavId">
              <ProductGroupMatrix
                class="product-group-cnt"
                v-for="[productGroupKey, productGroup] in groupGender.productGroups"
                :key="productGroupKey"
                :is-quantity-increase-allowed="isQuantityIncreaseAllowed"
                :product-group="productGroup"
                :default-size="defaultSize"
                :products-quantities="calculatedTotals.productTotals"
                :fabric-totals="calculatedTotals.fabricTotals"
                :product-group-totals="calculatedTotals.productGroupTotals"
                :show-wholesale-price="showWholesalePrice"
                @show-product-model-details="onShowProductModelDetails"
                @remove-product-group-quantities="onRemoveProductGroupQuantities(groupGender.name, productGroup.name)"
                @matrix-input-change="onProductQuantityChange" />
            </template>
          </template>

          <!-- Totals Display -->
          <TotalsDisplay
            :fabric-totals="calculatedTotals.fabricTotals"
            :main-group-totals="calculatedTotals.mainGroupTotals"
            :matrix-total="calculatedTotals.matrixTotal"
            :overall-total="overallTotal"
            :show-wholesale-price="showWholesalePrice"
            :show-availability-legends="showAvailabilityLabel" />
        </div>
      </div>
    </div>

    <!-- Product Model Details Popup -->
    <div v-if="productModelDetails">
      <NotificationMessage :size="'full'" class="popup-new" v-on:close="closeProductModelDetailsPopup">
        <ViewProductModelDetailsV2
          :hide-select-button="true"
          :is-selected="false"
          :product-model="productModelDetails"
          v-on:close="closeProductModelDetailsPopup" />
      </NotificationMessage>
    </div>

    <!-- Excel upload related modals -->
    <OverwriteWarningModal
      v-if="uploadExcelModalState === ExcelUploadModalState.OVERWRITE_WARNING"
      @close="setExcelModalState(null)"
      @continue="() => setExcelModalState(ExcelUploadModalState.UPLOAD)" />

    <ExcelUploadModal
      v-if="uploadExcelModalState === ExcelUploadModalState.UPLOAD"
      :is-downloading="downloadingTemplate"
      :is-uploading="uploadingTemplate"
      @close="setExcelModalState(null)"
      @download="onDownloadTemplate"
      @upload="handleUpload">
    </ExcelUploadModal>

    <ExcelUploadErrorModal
      v-if="uploadExcelModalState === ExcelUploadModalState.UPLOAD_FAILED"
      :error-reason="uploadErrorReason"
      @close="setExcelModalState(null)"
      @back-to-upload="setExcelModalState(ExcelUploadModalState.UPLOAD)" />

    <ExcelUploadPartialSuccessModal
      v-if="uploadExcelModalState === ExcelUploadModalState.PARTIAL_SUCCESS"
      :failed-rows="uploadFailedRows"
      :success-count="uploadSuccessCount"
      :total-count="uploadTotalCount"
      @close="setExcelModalState(null)"
      @continue="continueUpload"
      @discard="abortExcelUpload"
      @download="onDownloadFailedReport" />
  </div>
</template>

<script setup lang="ts">
// ── Imports ──────────────────────────────────────────────────────────
import {
  MatrixType,
  ProductGender,
  ProductMainGroups,
  resolveMatrixLocalSizeKey,
  ValidatorResult,
  type ProductMatrixQueryDto,
} from '@onrunning/backstage-models';
import { computed, getCurrentInstance, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import NotificationMessage from '@/components/common/NotificationMessage.vue';
import QuantitiesNavBar, { type NavBarItem } from '@/components/common/QuantitiesNavBar.vue';
import { useFormat } from '@/composables/format';
import { useAllowableAssortment } from '@/composables/useAllowableAssortment';
import { useGroupedMatrix } from '@/composables/useGroupedMatrix';
import { useMatrixProduct } from '@/composables/useMatrixProduct';
import { useNotification } from '@/composables/useNotification';
import { useOrderValidation } from '@/composables/useOrderValidation';
import { genderLetterMap } from '@/constants/matrix.constants';
import type { OrderEditType } from '@/constants/orderEditType';
import ExcelUploadErrorModal from '@/features/excel-order-upload/components/ExcelUploadErrorModal.vue';
import ExcelUploadModal from '@/features/excel-order-upload/components/ExcelUploadModal.vue';
import ExcelUploadPartialSuccessModal from '@/features/excel-order-upload/components/ExcelUploadPartialSuccessModal.vue';
import OverwriteWarningModal from '@/features/excel-order-upload/components/OverwriteWarningModal.vue';
import { useExcelTemplate } from '@/features/excel-order-upload/composables/useExcelTemplate';
import { ExcelUploadModalState, ExcelUploadOutcome } from '@/features/excel-order-upload/enums/excel-upload.enums';
import { utilsMixins } from '@/mixins/utils.mixins';
import type { ToastInstance } from '@/plugins/toast/types';
import { ProductImagesService } from '@/services/product-images.service';
import { ProductsMatrixService } from '@/services/products-matrix.service';
import { useOrderStore } from '@/stores/order';
import { useProductMatrixStore } from '@/stores/product-matrix';
import { useProductPricesStore } from '@/stores/product-prices';
import { useUserStore } from '@/stores/user';
import type { Fabric, MatrixTotals, ProductMatrixApiResponse, ProductModelDetails } from '@/types/matrix.interface';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { MessageType } from '../../plugins/toast/enums';
import ViewProductModelDetailsV2 from '../products/ViewProductModelDetailsV2.vue';
import { isProductMatrixValid } from './product-matrix-validator.util';
import ProductGroupMatrix from './ProductGroupMatrix.vue';
import TotalsDisplay from './TotalsDisplay.vue';

// ── Props ────────────────────────────────────────────────────────────
interface Props {
  isExistingOrder?: boolean;
  seasonStart?: string;
  orderNumber?: string;
  overallTotal?: number;
  isQuantityIncreaseAllowed?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isQuantityIncreaseAllowed: true,
});

// ── Services & Composables ───────────────────────────────────────────
const productsMatrixService = new ProductsMatrixService();
const productImagesService = new ProductImagesService();

const { t } = useI18n();
const userStore = useUserStore();
const { notifyError } = useNotification();
const productMatrixStore = useProductMatrixStore();
const route = useRoute();
const { isDraftEditMode, isNewDraft } = useOrderValidation(route.params.editType as OrderEditType);
const { formatNonLocalisedDateString, formatError } = useFormat();
const { getProductPrices } = useProductPricesStore();
const { shouldLoadPricesOnProductMatrix } = storeToRefs(useProductPricesStore());
const { orderType } = storeToRefs(useOrderStore());
const { onProductQuantityChange, onRemoveProductGroupQuantities, updateMatrixTotal, matrixType } = useMatrixProduct();
const { fetchAllowableAssortments } = useAllowableAssortment();
const toast = getCurrentInstance()?.appContext.config.globalProperties.$toast as ToastInstance;

// ── State ────────────────────────────────────────────────────────────
const error = ref(false);
const loading = ref(false);
const selectedNavId = ref<string | null>(null);
const productModelDetails = ref<ProductModelDetails | null>(null);
const productMatrix = ref<ProductMatrixApiResponse | null>(null);
const showWholesalePrice = ref(true);
const productGroupNumbers = ref<string[]>([]);
const mainGroupGenderNavBarStickyOffset = ref(0);
const uploadExcelModalState = ref<ExcelUploadModalState | null>(null);

// ── Computed ─────────────────────────────────────────────────────────
const isCaseMatrix = computed(() => matrixType.value === MatrixType.warranty || matrixType.value === MatrixType.return);

const increaseQuantitiesNotAllowedMessage = computed(() => {
  return t('matrix.increase_quantities_is_not_allowed', { orderType: orderType.value ?? '' });
});

const selectedDate = computed((): string | undefined => {
  const orderDate = productMatrixStore.activeMatrixOrder?.date;
  if (!orderDate) {
    return;
  }

  return formatNonLocalisedDateString(orderDate);
});

const { groupedMatrix, showAvailabilityLabel } = useGroupedMatrix(
  productMatrix,
  computed(() => ({
    orderDate: selectedDate.value,
    matrixType: matrixType.value,
    seasonStart: props.seasonStart,
    isExistingOrder: props.isExistingOrder,
  })),
);

const defaultSize = computed((): string => resolveMatrixLocalSizeKey(userStore.selectedAccount?.billingCountry));

const navBarItems = computed<NavBarItem[]>(() => {
  const items: NavBarItem[] = [];
  for (const [mainGroupKey] of groupedMatrix.value) {
    items.push({
      id: mainGroupKey,
      title: t(`matrix.tab_name.${mainGroupKey}`),
      selected: selectedNavId.value === mainGroupKey,
      total: calculatedTotals.value.groupGenderTotals[mainGroupKey] ?? 0,
    });
  }

  return items.sort((a, b) => b.title.localeCompare(a.title));
});

const calculatedTotals = computed((): MatrixTotals => {
  const productTotals: Record<string, number> = {};
  const fabricTotals: Record<string, number> = {};
  const productGroupTotals: Record<string, number> = {};
  const mainGroupTotals: Record<string, number> = {};
  const groupGenderTotals: Record<string, number> = {};

  let matrixTotal = 0;

  for (const { productId, quantity } of productMatrixStore.selectedProducts) {
    const product = productMatrixStore.products[productId];
    if (!product) continue;

    const productGroupKey = `${product.gender}-${product.productGroupName}`;
    const groupGenderKey =
      product.mainGroup === ProductMainGroups.accessories
        ? `${product.mainGroup}-${genderLetterMap[ProductGender.unisex]}`
        : `${product.mainGroup}-${genderLetterMap[product.gender]}`;

    productTotals[product.productId] = quantity ?? 0;
    fabricTotals[product.productGroupNumber] = (fabricTotals[product.productGroupNumber] ?? 0) + quantity;

    if (product?.disabled) {
      continue;
    }

    productGroupTotals[productGroupKey] = (productGroupTotals[productGroupKey] ?? 0) + quantity;
    groupGenderTotals[groupGenderKey] = (groupGenderTotals[groupGenderKey] ?? 0) + quantity;
    mainGroupTotals[product.mainGroup] = (mainGroupTotals[product.mainGroup] ?? 0) + quantity;
    matrixTotal += quantity;
  }

  return {
    matrixTotal,
    fabricTotals,
    productGroupTotals,
    mainGroupTotals,
    productTotals,
    groupGenderTotals,
  };
});

const {
  downloadingTemplate,
  onDownloadTemplate,
  showUploadButton,
  uploadFailedRows,
  uploadingTemplate,
  uploadSuccessCount,
  uploadTotalCount,
  uploadErrorReason,
  switchedToMultiplePreOrder,
  onUploadTemplate,
  onUploadContinue,
  onUploadAbort,
  onDownloadFailedReport,
} = useExcelTemplate(productMatrix, defaultSize, matrixType, loading, error, isNewDraft.value);

// ── Watchers ─────────────────────────────────────────────────────────
// When excel order upload results in switching the orderType, the offset needs to be recalculated
watch(orderType, async () => {
  await nextTick();
  recalculateStickyOffset();
});

watch(
  navBarItems,
  (items) => {
    if (items.length && !selectedNavId.value) {
      selectedNavId.value = items[0].id;
    }
  },
  { once: true },
);

watch(
  () => calculatedTotals.value.matrixTotal,
  (newTotal) => {
    updateMatrixTotal(newTotal);
  },
  { immediate: true },
);

watch(selectedDate, (newDate) => {
  if (newDate && (matrixType.value === MatrixType.order || matrixType.value === MatrixType.lastSeason)) {
    fetchProductMatrix();
  }
});

watch(uploadExcelModalState, (state) => {
  if (state === ExcelUploadModalState.UPLOAD_SUCCESS) {
    toast.show({
      message: t('matrix.toast.message.success'),
      type: MessageType.SUCCESS,
      title: t('matrix.toast.title.success'),
    });
    setExcelModalState(null);
  }
});

watch(switchedToMultiplePreOrder, (val) => {
  if (val) {
    switchedToMultiplePreOrder.value = false;
    toast.show({
      message: t('matrix.toast.message.order_type_changed'),
      type: MessageType.INFO,
      title: t('matrix.toast.title.order_type_changed'),
    });
  }
});

// ── Lifecycle ────────────────────────────────────────────────────────
onMounted(async () => {
  loading.value = true;
  if (productMatrixStore.productModels.size === 0 && orderType.value && selectedDate.value) {
    await fetchAllowableAssortments(orderType.value, selectedDate.value, props.orderNumber);
  }

  await fetchProductMatrix();
  recalculateStickyOffset();

  if (isDraftEditMode.value && !userStore.accountIsDistributor()) {
    productMatrixStore.updateDateIfLaunchDateMoveToLater();
  }
});

// ── Event Handlers ───────────────────────────────────────────────────
const onNavBarSelected = (itemId: string) => {
  selectedNavId.value = itemId;
};

const onShowProductModelDetails = async (product: Fabric) => {
  const imagesResult = await productImagesService.getProductModelImages(product.id);

  if (imagesResult.isValid() && imagesResult.model && imagesResult.model.length > 0) {
    productModelDetails.value = {
      name: product.name,
      defaultVariantImages: imagesResult.model,
      defaultImage: imagesResult.model[0],
    };
  }
};

const closeProductModelDetailsPopup = () => {
  productModelDetails.value = null;
};

const toggleShowWholesalePrice = () => {
  showWholesalePrice.value = !showWholesalePrice.value;
};

const onExcelUploadClick = async () => {
  if (calculatedTotals.value.matrixTotal > 0) {
    setExcelModalState(ExcelUploadModalState.OVERWRITE_WARNING);
  } else {
    setExcelModalState(ExcelUploadModalState.UPLOAD);
  }
};

const handleUpload = async (file: File): Promise<void> => {
  const result = await onUploadTemplate(file);

  setExcelModalState(outcomeToModalState[result]);
};

const abortExcelUpload = () => {
  onUploadAbort();
  setExcelModalState(ExcelUploadModalState.UPLOAD);
};

const continueUpload = async () => {
  const result = await onUploadContinue();
  if (result === ExcelUploadOutcome.UPLOAD_SUCCESS) {
    setExcelModalState(ExcelUploadModalState.UPLOAD_SUCCESS);
  } else {
    setExcelModalState(ExcelUploadModalState.UPLOAD_FAILED);
  }
};

// ── Methods ──────────────────────────────────────────────────────────
function recalculateStickyOffset() {
  mainGroupGenderNavBarStickyOffset.value = utilsMixins.calculateDivHeight([
    '#addEditNavBar',
    '#matrixRelatedOrdersSelector',
  ]);
}

async function fetchProductMatrix(): Promise<void> {
  try {
    const itemGroupNames = Array.from(productMatrixStore.selectedProductModelsNames);
    const itemNumbers = Array.from(productMatrixStore.selectedItemNumbers);

    let data: ValidatorResult<ProductMatrixApiResponse>;

    if (isCaseMatrix.value) {
      data = await productsMatrixService.getProductsMatrixForCases(
        userStore.selectedAccount.id,
        userStore.accountIsDistributor(),
        itemGroupNames,
      );
    } else {
      if (!selectedDate.value) {
        loading.value = false;

        return;
      }

      const productMatrixQuery = {
        itemGroupNames,
        itemNumbers: itemNumbers.length > 0 ? itemNumbers : undefined,
        orderDate: selectedDate.value,
        matrixType: matrixType.value,
        orderNumber: props.orderNumber,
      } as ProductMatrixQueryDto;

      data = await productsMatrixService.getProductsMatrixV3(userStore.selectedAccount.id, productMatrixQuery);
    }

    if (!isProductMatrixValid(data?.model)) {
      throw new Error(formatError(data.errors) || t('matrix.error.no_products_available'));
    }
    const result = data.model;

    productGroupNumbers.value = [...new Set(result?.products.map((product) => product.productGroupNumber) ?? [])];

    if (productGroupNumbers.value.length > 0 && shouldLoadPricesOnProductMatrix.value && !isCaseMatrix.value) {
      getProductPrices({
        productGroupNumbers: productGroupNumbers.value,
        requestReceiptDate: selectedDate.value!,
        orderType: orderType.value!,
      });
    }

    productMatrix.value = result;
    error.value = false;
  } catch (err: any) {
    error.value = true;
    notifyError(err.message || t('global.error'));
  } finally {
    loading.value = false;
  }
}

function setExcelModalState(state: ExcelUploadModalState | null) {
  uploadExcelModalState.value = state;
}

const outcomeToModalState: Record<ExcelUploadOutcome, ExcelUploadModalState> = {
  [ExcelUploadOutcome.UPLOAD_SUCCESS]: ExcelUploadModalState.UPLOAD_SUCCESS,
  [ExcelUploadOutcome.PARTIAL_SUCCESS]: ExcelUploadModalState.PARTIAL_SUCCESS,
  [ExcelUploadOutcome.UPLOAD_FAILED]: ExcelUploadModalState.UPLOAD_FAILED,
};
</script>

<style lang="scss" scoped>
.products-quantities-selector-v3 {
  .products-matrix {
    max-width: unset;
    padding: 0;
    width: 100%;
  }

  .is-invalid {
    border: none !important;
    color: var(--special-red-color-darker) !important;

    input::placeholder {
      color: var(--special-red-color-darker) !important;
    }
  }

  .warning-banner {
    padding: 20px;
    margin-bottom: 20px;
    color: var(--primary-text-color);
    background-color: var(--special-orange-color);

    @include breakpoint(medium) {
      margin-top: 10px;
      margin-bottom: 10px;
    }
  }

  .navbar-background {
    @include navbar-background;

    top: 83px;

    @include breakpoint(medium) {
      top: 63px;
    }

    @include breakpoint(small) {
      top: 173px;
    }

    &.multiple {
      top: 168.8px;
    }
  }

  .product-groups-cnt {
    @include common-container;
    padding: 16px 20px;

    @include breakpoint(medium) {
      padding: 20px 2px;
    }
  }

  .product-group-cnt {
    margin-bottom: 10px;
    background-color: var(--primary-bg-color);

    @include breakpoint(medium) {
      font-size: 14px;
      padding: 0;
    }
  }

  .hidden-file-input {
    display: none;
  }
}
</style>
